import s from './appointment-modal.module.scss'
import { Modal } from '@shared/ui/modal/modal';
import { Button } from '@shared/ui/button/button';
import { IconMessage, IconCalendarTime, IconClock, IconStar, IconStarFilled } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { classes } from '@shared/api/services';
import { useMemo } from 'react';
import { addMinutes, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { FetchingDiv } from '@shared/ui/fetching-div/fetching-div';
import { useAppointment } from '@entities/appointment/api/useAppointment';
import { useProfile } from '@shared/api/useProfile';
import { getFullName } from '@utils/lib/getFullname';
import { useAppointmentModal } from '../../model/useAppointmentModal';
import { useRemoveAppointmentModal } from '../../../remove-appointment/model/use-remove-appointment-modal';
import { TZDate } from '@date-fns/tz';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { SupportButton } from '@shared/ui/support-button';
import { useAddReviewModal } from '@features/student/add-review/model/useAddReviewModal';
import { TextCollapse } from '@shared/ui/text-collapse/text-collapse';
import { getCDNPath } from '@utils/lib/getCDNpath';
import { canCancelLesson, CANCEL_GRACE_MINUTES } from '@utils/lib/canCancelLesson';

export const AppointmentModal = () => {
	const { isOpen, close, modalParams } = useAppointmentModal()
	const { open: openRemoveAppointment } = useRemoveAppointmentModal()
	const { open: openAddReview } = useAddReviewModal()

	const appointmentId = modalParams?.appointmentId
	const { data: appointment, isFetching } = useAppointment(appointmentId)
	const { data: profile, isLoading: isProfileLoading } = useProfile()

	const teacher = appointment?.teacher
	const student = appointment?.student
	const isTeacher = profile?.role === 'teacher'
	const payed = true

	const navigate = useNavigate()

	// Дату считаем только после загрузки profile.timezone — иначе TZDate
	// возьмет часовой пояс браузера вместо часового пояса пользователя
	const timezone = profile?.timezone
	const startsAt = appointment?.starts_at
	const isDateLoading = isProfileLoading || (!!profile && !timezone)

	const date = useMemo(
		() => startsAt && timezone ? new TZDate(startsAt, timezone) : null,
		[startsAt, timezone]
	)

	const { dateFormat, timeStart, timeEnd, dayKey } = useMemo(() => {
		if (!date) return { dateFormat: '', timeStart: '', timeEnd: '', dayKey: null }

		return {
			dateFormat: format(date, 'd MMMM', { locale: ru }),
			timeStart: format(date, 'HH:mm'),
			timeEnd: format(addMinutes(date, 50), 'HH:mm'),
			dayKey: format(date, 'yyyy-MM-dd'),
		}
	}, [date])

	const todayKey = useMemo(
		() => timezone ? format(new TZDate(new Date(), timezone), 'yyyy-MM-dd') : null,
		[timezone]
	)

	// Прошедшее занятие (по UTC-времени окончания, не зависит от таймзоны)
	const isPast = !!appointment?.ends_at
		? new Date(appointment.ends_at).getTime() < Date.now()
		: false
	const canReview = !isTeacher && isPast && appointment?.status !== 'cancelled'
	const feedbackRating = appointment?.feedback_rating

	const openReviewModal = () => openAddReview({
		appointmentId: appointment?.id,
		review: {
			feedback_rating: appointment?.feedback_rating,
			feedback_message: appointment?.feedback_message,
		},
	})

	// Строки yyyy-MM-dd сравниваются лексикографически корректно
	const isToday = dayKey !== null && dayKey === todayKey
	const canCancel = !isDateLoading && canCancelLesson({
		status: appointment?.status,
		startsAt,
		createdAt: appointment?.created_at,
		timezone,
		isTeacher,
	})

	const modalStyle = {
		content: {
			width: 'calc(100% - 40px)',
			maxWidth: '420px',
			maxHeight: 'calc(100svh - 40px)',
			padding: '20px',
			overflow: 'auto',
		},
	}

	const avatarImage = isTeacher
		? student?.avatar?.image
		: teacher?.avatar?.image

	return (
		<Modal isOpen={isOpen} close={close} title={'Занятие'} style={modalStyle}>
			<FetchingDiv className={s.card} isFetching={isFetching || isDateLoading}>
				{appointment && (
					<motion.div
						className={s.wrapper}
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<div className={s.profileCard}>
							<div className={s.avatar}>
								<img
									src={avatarImage ? getCDNPath(avatarImage) : getCDNPath('images/default_avatar.png')}
									alt=""
								/>
							</div>
							<div className={s.profileInfo}>
								<span className={s.name}>
									{getFullName(isTeacher ? student : teacher)}
								</span>
								{!isTeacher ? (
									<div className={s.profileMeta}>
										<span><TextWithEmoji text={'🌟'} /> {teacher?.rating}</span>
										<span><TextWithEmoji text={'💬'} /> {teacher?.reviews?.length || '0'}</span>
									</div>
								) : (
									<span className={s.role}>Ученик</span>
								)}
							</div>
							{payed ? (
								<Button
									label={<IconMessage size={16} />}
									size='small'
									className={s.messageBtn}
									onClick={() => {
										navigate('/messenger', { state: { id_user_in_chat: isTeacher ? student?.id : teacher?.id } })
										close()
									}}
								/>
							) : null}
						</div>

						<div className={s.tags}>
							<div className={classNames(s.tag, s.dateTag, { [s.today]: isToday })}>
								{isToday ? `Сегодня, ${dateFormat}` : dateFormat}
							</div>
							<div className={s.tag}>
								<IconCalendarTime size={14} />
								<span>{timeStart}-{timeEnd}</span>
							</div>
							<div className={s.tag}>
								<IconClock size={14} />
								<span>50 мин.</span>
							</div>
							<div className={s.tag}>
								<TextWithEmoji text={appointment?.instrument?.name} />
							</div>
						</div>

						{canReview && feedbackRating ? (
							<div className={s.reviewBlock}>
								<div className={s.reviewHead}>
									<span className={s.reviewTitle}>Ваш отзыв</span>
									<div className={s.reviewStars}>
										{[1, 2, 3, 4, 5].map((star) =>
											star <= feedbackRating
												? <IconStarFilled key={star} size={16} className={s.starActive} />
												: <IconStar key={star} size={16} className={s.star} />
										)}
								</div>
							</div>
							{appointment?.feedback_message ? (
								<TextCollapse className={s.reviewText} maxHeight={46.8}>
									{appointment.feedback_message}
								</TextCollapse>
							) : null}
						</div>
						) : null}

						<div className={s.actions}>
							{(payed || isTeacher) ? (
								<Button
									label={'Перейти к занятию'}
									size='small'
									className={s.actionBtn}
									onClick={() => {
										navigate('/appointment/' + appointment?.id)
										close()
									}}
								/>
							) : (
								<Button
									label={'Оплатить'}
									size='small'
									className={s.actionBtn}
									onClick={() => {
										classes.payForAppointment(appointment?.id).then(payment => {
											window.open(payment.data, '_self')
										})
										close()
									}}
								/>
							)}

							{canReview && !feedbackRating && (
								<Button
									theme='secondary'
									label={'Оцените прошедшее занятие'}
									size='small'
									className={s.actionBtn}
									onClick={openReviewModal}
								/>
							)}

							{appointment?.status === 'active' ? (
								<Button
									disabled={!canCancel}
									theme='secondary'
									label={'Отменить занятие'}
									size='small'
									className={s.actionBtn}
									onClick={() => openRemoveAppointment({ appointmentId: appointment?.id })}
								/>
							) : null}
						</div>

						{!isTeacher && (
							<p className={s.hint}>
								*Занятие можно отменить не позже чем за день до начала занятия
							или в течение {CANCEL_GRACE_MINUTES} минут после бронирования.
							</p>
						)}


						<div className={s.support}>
							<SupportButton
								label={'Нужна помощь?'}
								theme='clear'
								size='small'
								shape='pill'
								placement='top-end'
								buttonClassName={s.supportBtn}
							/>
						</div>
						</motion.div>
				)}
			</FetchingDiv>
		</Modal>
	)
}
